import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import DataTable from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getOrderDetailAttributeHandler from "@tKgpco/Handlers/OrderDetails/getOrderDetailsAttributeHandler";
import { getOrderAttributeFn } from "@tKgpco/Services/OrderDetailsAttributeService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ orderNumber?: string }>(location.search);
    return {
        orderNumber: parsedQuery.orderNumber,
        orderAttributes: state.KGPState.OrderHistoryAttributeStore.orderAttributeCollection,
        orderAttributesLoading: state.KGPState.OrderHistoryAttributeStore.isLoading,
    };
};

const mapDispatchToProps = {
    getOrderDetailAttributeHandler,
};

export interface OrderDetailsAttributeInfoStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    status?: TypographyProps;
    tHeader?: DataTableCellProps;
}

export const webOrderStyles: OrderDetailsAttributeInfoStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;
            margin-top: 15px;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
            font-size: 16px;
        `,
    },
    status: {
        color: "primary",
        css: css`
            text-decoration: underline;
            cursor: pointer;
        `,
    },
    tHeader: {
        typographyProps: {
            transform: "uppercase",
        },
    },
};

const styles = webOrderStyles;
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderDetailsAttributeInfo = ({
    getOrderDetailAttributeHandler,
    orderNumber,
    orderAttributes,
    orderAttributesLoading,
}: Props) => {
    const { value: order } = useContext(OrderStateContext);
    const [openAttributeModal, setOpenAttributeModal] = useState(false);
    if (!order || !orderNumber) {
        return null;
    }

    const getOrderAttribute = () => {
        setOpenAttributeModal(true);
        getOrderDetailAttributeHandler({
            orderNumber,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography
                {...styles.status}
                aria-labelledby="OrderDetailsAttributeInfo"
                data-test-selector="OrderDetailsAttributeInfo"
                onClick={getOrderAttribute}
            >
                {translate("Attribute Info")}
            </Typography>
            <Modal
                headline={translate("Order Attribute Info")}
                size={500}
                isOpen={openAttributeModal}
                handleClose={() => setOpenAttributeModal(false)}
            >
                {orderAttributesLoading ? (
                    <StyledWrapper
                        style={{
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        <LoadingSpinner size={30} />
                    </StyledWrapper>
                ) : !orderAttributes?.length ? (
                    <Typography>{translate("No Results")}</Typography>
                ) : (
                    <DataTable>
                        <DataTableBody>
                            {orderAttributes.map(attribute => (
                                <DataTableRow key={attribute.attribute}>
                                    <DataTableCell {...styles.tHeader}>{attribute.attribute}</DataTableCell>
                                    <DataTableCell>{attribute.attributeValue || "N/A"}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                )}
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsAttributeInfo),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
